import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    questionType: String,
    assessmentId: Number
  }
  static targets = ["nextButton", "previousButton"]

  connect() {
    this.currentAnswer = null
    this.isProcessing = false

    // Initialize based on question type
    if (this.questionTypeValue === "current_time_allocation") {
      this.initializeAllocationPie()
    } else if (this.questionTypeValue === "energy_efficiency") {
      this.initializeEnergySlider()
    } else if (this.questionTypeValue === "trend_over_time") {
      this.initializeTrend()
    }

    // Update button state after initialization
    setTimeout(() => {
      this.updateNextButton()
    }, 100)
  }

  // Question A: Draggable pie chart (allocation-pie controller dispatches change on connect/drag)
  initializeAllocationPie() {
    // currentAnswer is set by allocationPieChanged when the pie mounts
    this.updateNextButton()
  }

  allocationPieChanged(event) {
    this.currentAnswer = {
      personal: event.detail.personal,
      strategic: event.detail.strategic,
      tactical: event.detail.tactical
    }
    this.updateNextButton()
  }

  // Question B: Single slider
  initializeEnergySlider() {
    const slider = this.element.querySelector(".energy-slider")
    if (!slider) return

    // Load existing value if any
    const existingValue = parseFloat(slider.value) || 50
    this.currentAnswer = { value: existingValue }
    this.updateEnergyDisplay(existingValue)

    slider.addEventListener("input", (e) => {
      const value = parseFloat(e.target.value)
      this.currentAnswer = { value: value }
      this.updateEnergyDisplay(value)
      this.updateNextButton()
    })

    this.updateNextButton()
  }

  updateEnergySlider(event) {
    const value = parseFloat(event.target.value)
    this.currentAnswer = { value: value }
    this.updateEnergyDisplay(value)
    this.updateNextButton()
  }

  updateEnergyDisplay(value) {
    const display = this.element.querySelector("#energy-value")
    if (display) {
      display.textContent = `${Math.round(value)}%`
    }
  }

  // Question C: Radio buttons
  initializeTrend() {
    // Check for existing selection
    const selectedRadio = this.element.querySelector('input[name="trend_direction"]:checked')
    if (selectedRadio) {
      this.currentAnswer = {
        trend_direction: selectedRadio.value,
        trend_notes: this.element.querySelector('textarea[name="trend_notes"]')?.value || ""
      }
    }

    // Update character count
    const textarea = this.element.querySelector('textarea[name="trend_notes"]')
    if (textarea) {
      this.updateCharCount()
      textarea.addEventListener("input", () => {
        this.updateCharCount()
        if (this.currentAnswer) {
          this.currentAnswer.trend_notes = textarea.value
        }
      })
    }

    this.updateNextButton()
  }

  selectTrend(event) {
    const radio = event.currentTarget
    const value = radio.value

    // Update visual state
    this.element.querySelectorAll(".radio-option").forEach(opt => {
      opt.classList.remove("border-primary", "bg-primary-50")
      opt.classList.add("border-gray-300")
    })
    radio.closest(".radio-option")?.classList.add("border-primary", "bg-primary-50")

    // Store answer
    if (!this.currentAnswer) {
      this.currentAnswer = {}
    }
    this.currentAnswer.trend_direction = value

    const textarea = this.element.querySelector('textarea[name="trend_notes"]')
    if (textarea) {
      this.currentAnswer.trend_notes = textarea.value
    }

    this.updateNextButton()
  }

  updateNotes(event) {
    if (!this.currentAnswer) {
      this.currentAnswer = {}
    }
    this.currentAnswer.trend_notes = event.target.value
  }

  updateCharCount() {
    const textarea = this.element.querySelector('textarea[name="trend_notes"]')
    const counter = this.element.querySelector("#notes-char-count")
    if (textarea && counter) {
      counter.textContent = textarea.value.length
    }
  }

  scrollQuestionToTop(parent) {
    if (!parent) return
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
      const newContainer = parent.firstElementChild
      const scrollable = newContainer?.querySelector(".overflow-y-auto")
      if (scrollable) scrollable.scrollTo({ top: 0, behavior: "smooth" })
    })
  }

  updateNextButton() {
    const nextButton = this.nextButtonTarget
    if (!nextButton) return

    let canProceed = false

    if (this.questionTypeValue === "current_time_allocation") {
      if (this.currentAnswer) {
        const total = Object.values(this.currentAnswer).reduce((sum, val) => sum + val, 0)
        canProceed = total === 100
      }
    } else if (this.questionTypeValue === "energy_efficiency") {
      canProceed = this.currentAnswer !== null && this.currentAnswer.value !== undefined
    } else if (this.questionTypeValue === "trend_over_time") {
      canProceed = this.currentAnswer !== null &&
        this.currentAnswer.trend_direction !== undefined &&
        this.currentAnswer.trend_direction !== null &&
        this.currentAnswer.trend_direction !== ""
    }

    nextButton.disabled = !canProceed
  }

  async nextQuestion() {
    if (this.isProcessing) return
    if (!this.currentAnswer) {
      alert("Please provide an answer before continuing.")
      return
    }

    // Validate sliders sum to 100
    if (this.questionTypeValue === "current_time_allocation") {
      const total = Object.values(this.currentAnswer).reduce((sum, val) => sum + val, 0)
      if (total !== 100) {
        alert("Time allocation must sum to exactly 100%")
        return
      }
    }

    this.isProcessing = true
    const nextButton = this.nextButtonTarget
    if (nextButton) {
      nextButton.disabled = true
      nextButton.textContent = "Processing..."
    }

    try {
      const url = `/right_role_assessment/next_question`
      const formData = new FormData()
      formData.append("current_role_question_type", this.questionTypeValue)

      if (this.questionTypeValue === "current_time_allocation") {
        formData.append("response_data", JSON.stringify(this.currentAnswer))
      } else if (this.questionTypeValue === "energy_efficiency") {
        formData.append("value", this.currentAnswer.value)
      } else if (this.questionTypeValue === "trend_over_time") {
        formData.append("trend_direction", this.currentAnswer.trend_direction)
        formData.append("trend_notes", this.currentAnswer.trend_notes || "")
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData,
        redirect: "manual"
      })

      // Handle redirects (302) - this happens when it's the last question
      if (response.status === 302 || response.status === 0) {
        const redirectUrl = response.headers.get("Location")
        if (redirectUrl) {
          window.location.href = redirectUrl
        } else {
          window.location.href = `/right_role_assessment`
        }
        return
      }

      if (response.ok) {
        const contentType = response.headers.get("content-type")
        if (contentType && contentType.includes("text/html")) {
          const html = await response.text()
          const parent = this.element.parentElement
          const oldElement = this.element
          oldElement.outerHTML = html
          this.scrollQuestionToTop(parent)
        } else {
          const data = await response.json()
          if (data.error) {
            alert(data.error)
            this.isProcessing = false
            if (nextButton) {
              nextButton.disabled = false
              nextButton.textContent = "Next →"
            }
          } else if (data.redirect) {
            window.location.href = data.redirect
          }
        }
      } else {
        let errorMessage = `An error occurred: ${response.status}`
        try {
          const errorText = await response.text()
          if (errorText) {
            try {
              const errorData = JSON.parse(errorText)
              errorMessage = errorData.error || errorData.message || errorMessage
            } catch {
              if (errorText.length < 200) {
                errorMessage = errorText
              }
            }
          }
        } catch (e) {
          console.error("Error reading response:", e)
        }

        console.error("Error response:", response.status, errorMessage)
        alert(errorMessage)
        this.isProcessing = false
        if (nextButton) {
          nextButton.disabled = false
          nextButton.textContent = "Next →"
        }
      }
    } catch (error) {
      console.error("Error advancing to next question:", error)
      alert("An error occurred. Please try again.")
      this.isProcessing = false
      if (nextButton) {
        nextButton.disabled = false
        nextButton.textContent = "Next →"
      }
    }
  }

  async previousQuestion() {
    if (this.isProcessing) return

    this.isProcessing = true

    try {
      const url = `/right_role_assessment/previous_question`
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        redirect: "manual"
      })

      if (response.ok) {
        const contentType = response.headers.get("content-type")
        if (contentType && contentType.includes("text/html")) {
          const html = await response.text()
          const parent = this.element.parentElement
          this.element.outerHTML = html
          this.scrollQuestionToTop(parent)
        }
      } else {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      console.error("Error loading previous question:", error)
      this.isProcessing = false
    }
  }
}
